import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Label } from 'designSystem/components/Label'
import { Textarea } from 'designSystem/components/Textarea'
import React, { useCallback, useState } from 'react'

export type DeferPageMetadataDialogProps = {
  open: boolean
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onDefer: (reason: string) => void | Promise<void>
}

export function DeferPageMetadataDialog({
  open,
  isSaving,
  onOpenChange,
  onDefer,
}: DeferPageMetadataDialogProps): React.ReactElement {
  const { t } = useLingui()
  const [reason, setReason] = useState('')
  const trimmedReason = reason.trim()
  const canSubmit = trimmedReason.length > 0 && !isSaving

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setReason('')
      onOpenChange(nextOpen)
    },
    [onOpenChange]
  )

  const handleReasonChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setReason(event.currentTarget.value)
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return
    await onDefer(trimmedReason)
  }, [canSubmit, onDefer, trimmedReason])

  const handleCancel = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size='sm'>
        <DialogHeader>
          <DialogTitle>{t`Defer this page`}</DialogTitle>
          <DialogDescription>
            {t`A reason is required. Deferred pages stay out of your default unlabeled pool.`}
          </DialogDescription>
        </DialogHeader>
        <Label htmlFor='page-metadata-eval-defer-reason'>{t`Reason`}</Label>
        <Textarea
          id='page-metadata-eval-defer-reason'
          value={reason}
          disabled={isSaving}
          onChange={handleReasonChange}
        />
        <DialogFooter>
          <Button
            type='button'
            variant='inputRaised'
            disabled={isSaving}
            onClick={handleCancel}>
            {t`Cancel`}
          </Button>
          <Button
            type='button'
            variant='primaryRaised'
            disabled={!canSubmit}
            onClick={handleSubmit}>
            {isSaving ? t`Deferring…` : t`Defer`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
